// i18n deferred: admin/blog/legal pages are intentionally excluded from Step 2 migration.
import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthRoles } from '../hooks/useAuthRoles';
import { InvitationsSection } from '../components/admin/corporate-invitations/InvitationsSection';
import { JobCostOverview } from '../components/admin/job-costs/JobCostOverview';
import { CompletedInterviewsList } from '../components/admin/completed-interviews/CompletedInterviewsList';
import { InterviewLinksList } from '../components/admin/interview-links/InterviewLinksList';
import { DeletableJobsList } from '../components/admin/deletable-jobs';
import { UsageManagement } from '../components/admin/UsageManagement';
import { WorkspaceProfilesList } from '../components/admin/workspace-profiles/WorkspaceProfilesList';
import { CouponManagement } from '../components/admin/CouponManagement';
import { AdminUserCleanupSection } from './AdminUserCleanupPage';
import { SessionsManagement } from '../components/admin/SessionsManagement';
import { Tabs } from '@/components/ui/atoms/Tabs';
import { cn } from '@/utils/cn';
import {
  DollarSign,
  CheckCircle2,
  Link2,
  Mail,
  FolderOpen,
  Target,
  Building2,
  Ticket,
  UserMinus,
  LogOut,
  ChevronLeft,
  ChevronRight,
  type LucideIcon
} from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';

type TabId =
  | 'costs'
  | 'interviews'
  | 'links'
  | 'invitations'
  | 'profiles'
  | 'jobs'
  | 'usage'
  | 'coupons'
  | 'user_cleanup'
  | 'sessions';

interface Tab {
  id: TabId;
  label: string;
  icon: LucideIcon;
  description: string;
}

const tabs: Tab[] = [
  {
    id: 'costs',
    label: 'Job Costs',
    icon: DollarSign,
    description: 'View and analyze job costs and pricing details',
  },
  {
    id: 'interviews',
    label: 'Completed Interviews',
    icon: CheckCircle2,
    description: 'Download interview data and regenerate assessments',
  },
  {
    id: 'links',
    label: 'Interview Links',
    icon: Link2,
    description: 'Manage interview links and extend expiration dates',
  },
  {
    id: 'invitations',
    label: 'Corporate Invitations',
    icon: Mail,
    description: 'Create and manage corporate access invitations',
  },
  {
    id: 'profiles',
    label: 'Workspace Profiles',
    icon: Building2,
    description: 'View onboarding and workspace details collected at plan activation',
  },
  {
    id: 'jobs',
    label: 'Job Management',
    icon: FolderOpen,
    description: 'Delete jobs and reload interview assessments',
  },
  {
    id: 'usage',
    label: 'Usage Management',
    icon: Target,
    description: 'Manage user plans, limits, and usage tracking',
  },
  {
    id: 'coupons',
    label: 'Coupons',
    icon: Ticket,
    description: 'Create and manage discount coupon codes',
  },
  {
    id: 'user_cleanup',
    label: 'User Cleanup',
    icon: UserMinus,
    description: 'Wipe a user end-to-end so the email can sign up again',
  },
  {
    id: 'sessions',
    label: 'Sessions',
    icon: LogOut,
    description: 'Force-disconnect a user or kick all non-admin sessions',
  },
];

export const AdminPage = () => {
  const { isAdmin, isLoading, isAuthenticated } = useAuthRoles();
  const [activeTab, setActiveTab] = useState<TabId>('costs');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Stable item list for the Tabs atom (icons are static module-level config).
  const tabItems = useMemo(
    () =>
      tabs.map((tab) => {
        const Icon = tab.icon;
        return {
          id: tab.id,
          label: tab.label,
          title: tab.label,
          icon: <Icon className="w-5 h-5 shrink-0" />,
        };
      }),
    [],
  );

  // If not authenticated, redirect to home
  if (!isLoading && !isAuthenticated) {
    return <Navigate to="/" />;
  }

  // If not admin, redirect to dashboard
  if (!isLoading && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-1">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gradient-purple border-t-transparent"></div>
          <p className="text-dark-invirtus font-heading font-semibold">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'costs':
        return <JobCostOverview isAdmin={isAdmin} isLoading={isLoading} />;
      case 'interviews':
        return <CompletedInterviewsList isAdmin={isAdmin} isLoading={isLoading} />;
      case 'links':
        return <InterviewLinksList isAdmin={isAdmin} isLoadingAuth={isLoading} />;
      case 'invitations':
        return <InvitationsSection isAdmin={isAdmin} isLoading={isLoading} />;
      case 'profiles':
        return <WorkspaceProfilesList isAdmin={isAdmin} isLoading={isLoading} />;
      case 'jobs':
        return <DeletableJobsList isAdmin={isAdmin} isLoadingAuth={isLoading} />;
      case 'usage':
        return <UsageManagement isAdmin={isAdmin} isLoading={isLoading} />;
      case 'coupons':
        return <CouponManagement />;
      case 'user_cleanup':
        return <AdminUserCleanupSection />;
      case 'sessions':
        return <SessionsManagement />;
      default:
        return null;
    }
  };

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="h-[calc(100vh-3.5rem)] md:h-[calc(100vh-3.5rem)] bg-background-gray flex">
      {/* Sidebar */}
      <div className={`bg-white border-r border-light-lila/30 transition-all duration-300 ease-in-out ${
        sidebarCollapsed ? 'w-20' : 'w-20 lg:w-80'
      }`}>

        {/* Sidebar Header */}
        <div className="p-4 border-b border-light-lila/30">
          <div className="flex items-center justify-between">
            <div className="lg:hidden w-8 h-8 bg-gradient-to-br from-gradient-purple to-gradient-pink rounded-lg flex items-center justify-center mx-auto">
              <Target className="w-4 h-4 text-white" />
            </div>
            
            {!sidebarCollapsed && (
              <div className="hidden lg:flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-gradient-purple to-gradient-pink rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-heading font-bold text-dark-invirtus">Admin</h1>
                  <p className="text-xs text-dark-invirtus/60 font-medium">Dashboard</p>
                </div>
              </div>
            )}
            {sidebarCollapsed && (
              <div className="hidden lg:flex w-8 h-8 bg-gradient-to-br from-gradient-purple to-gradient-pink rounded-lg items-center justify-center mx-auto">
                <Target className="w-4 h-4 text-white" />
              </div>
            )}
            
            {/* Collapse/Expand Button - Desktop only */}
            <Button
              type="icon"
              intent="muted"
              size="sm"
              className="hidden lg:flex"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              icon={sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
            />
          </div>
        </div>

        {/* Navigation — collapse chrome stays here; Tabs owns the tablist only. */}
        <div className="p-4">
          <Tabs
            aria-label="Admin sections"
            orientation="vertical"
            variant="default"
            size="md"
            value={activeTab}
            onValueChange={(id) => setActiveTab(id as TabId)}
            items={tabItems}
            className={cn(
              'gap-2',
              // Icon-only below lg and whenever the rail is collapsed on desktop.
              sidebarCollapsed
                ? '[&_[data-slot=tabs-text]]:hidden [&_[data-slot=tabs-trigger]]:justify-center [&_[data-slot=tabs-trigger]]:px-0'
                : '[&_[data-slot=tabs-text]]:hidden lg:[&_[data-slot=tabs-text]]:block',
            )}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Content Area */}
        <div className="flex-1 min-h-0 flex flex-col p-4 lg:p-6">
          {/* Page Header */}
          <div className="mb-6 shrink-0">
            <h2 className="text-3xl font-heading font-bold text-dark-invirtus flex items-center gap-3">
              <div className="w-1.5 h-10 bg-gradient-to-b from-gradient-purple to-gradient-pink rounded-full"></div>
              {activeTabData?.label}
            </h2>
            <p className="text-dark-invirtus/60 font-medium mt-2 ml-5">
              {activeTabData?.description}
            </p>
          </div>

          <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};